import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Brand config drives the header, PersonaPicker hero and primary accent color.
// Keeping the same route surface (Home, Catalog, etc.) — only visual chrome
// changes across brands. New brands can be added here without touching pages.
export const BRANDS = {
  flexspace: {
    id: "flexspace",
    label: "Flexspace",
    small: "Device Self Service",
    orgName: "Enterprise",
    orgFull: "Flexspace DaaS",
    wordmark: "Flexspace",
    logoLetter: "F",
    primary: "#0033A0",
    primaryDark: "#002573",
    primarySoft: "#0033A020",
    logoShape: "rounded",
    heroTitle: "Order the right hardware",
    heroAccent: "for the way you work.",
    heroBody:
      "Flexspace tailors your device catalogue to your role — from field engineers to product designers. Every order automatically opens a ServiceNow ticket for IT fulfillment.",
    footer: "© Flexspace · Enterprise Demo",
    ctaLabel: "Enter Flexspace",
  },
  chevron: {
    id: "chevron",
    label: "Chevron",
    small: "Chevron",
    orgName: "Chevron",
    orgFull: "Chevron U.S.A. Inc.",
    wordmark: "Flexspace",
    logoLetter: "C",
    primary: "#0033A0",
    primaryDark: "#002573",
    primarySoft: "#0033A020",
    logoShape: "chevron",
    heroTitle: "Order the right hardware",
    heroAccent: "for the way you work.",
    heroBody:
      "Flexspace tailors your device catalogue to your role — from engineers on the rig to designers in San Ramon. Every order automatically opens a ServiceNow ticket for IT fulfillment.",
    footer: "© Chevron U.S.A. Inc. — Internal use only.",
    ctaLabel: "Enter Flexspace",
  },
  daikin: {
    id: "daikin",
    label: "Daikin",
    small: "daikin",
    orgName: "Daikin",
    orgFull: "Daikin Industries",
    wordmark: "Flexspace",
    logoLetter: "D",
    primary: "#E60027",
    primaryDark: "#B60020",
    primarySoft: "#E6002720",
    logoShape: "rounded",
    heroTitle: "One portal for every device —",
    heroAccent: "from R&D labs to field service vans.",
    heroBody:
      "Flexspace tailors the device catalogue to each Daikin role — HVAC technicians in the field, engineers on the plant floor, R&D at Suzhou, or sales at Waller. Every request auto-opens a ServiceNow ticket for global IT fulfillment.",
    footer: "© Daikin · Flexspace Demo",
    ctaLabel: "Enter Flexspace",
  },
};

const STORAGE_KEY = "flexspace.brand";
const BrandingContext = createContext({ brand: BRANDS.flexspace, setBrand: () => {} });

export function BrandingProvider({ children }) {
  const [brandId, setBrandId] = useState(() => {
    if (typeof window === "undefined") return "flexspace";
    return localStorage.getItem(STORAGE_KEY) || "flexspace";
  });

  const brand = BRANDS[brandId] || BRANDS.flexspace;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, brand.id);
    // Expose brand colors as CSS variables so arbitrary Tailwind values can
    // reference them (e.g. bg-[color:var(--brand)]) if any page needs it.
    document.documentElement.style.setProperty("--brand", brand.primary);
    document.documentElement.style.setProperty("--brand-dark", brand.primaryDark);
  }, [brand]);

  const value = useMemo(() => ({ brand, setBrand: setBrandId }), [brand]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}
